import React from "react";
import { useLocation, useNavigate } from "react-router";
import { I } from "../redesign/kvpIcons";
import { Toast } from "../redesign/Toast";
import { useTelephonyOptional } from "../../context/TelephonyContext";
import { useResponsive } from "../../hooks/useResponsive";
import { useWindowManagerOptional } from "../windowManager/WindowManagerContext";
import { kontextJetzt, MeldeDialog, MeldeKontext } from "./MeldeDialog";
import { beobachteKlicks, erfasseStation } from "./seitenVerlauf";
import { ankerAus, begrenze, KnopfAnker, KnopfPos, ladePos, posAus, speicherePos, ZIEH_SCHWELLE } from "./knopfPosition";
import "./tickets.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/** Globaler Melden-Knopf, standardmäßig unten rechts (über Dock bzw. Anruf-Widget) — verschiebbar. */
export function MeldeButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const tel = useTelephonyOptional();
  const anrufOffen = !!tel?.activeCall && !tel.minimized;
  // Phone + offenes Fenster (T-199): Fenster sind dort Vollbild und tragen ihre
  // Knöpfe unten („Zuordnen", „Senden" …) — genau dort lag der Melden-Knopf und
  // fing die Tipps ab. Dann weicht er als Lasche an den rechten Rand aus.
  // Minimierte Fenster zählen nicht (wie die Phone-Leiste in NavigationLayout).
  const { isPhone } = useResponsive();
  const wm = useWindowManagerOptional();
  const fensterOffen = !!wm?.windows.some((w) => w.state !== "minimized" && !w.browserFenster);
  const [kontext, setKontext] = React.useState<MeldeKontext | null>(null);
  const [gemeldet, setGemeldet] = React.useState<number | null>(null);

  // Verschieben: Ziehen ab ZIEH_SCHWELLE px verschiebt, sonst ist es ein Klick.
  // `anker` ist die WUNSCHposition (Abstand zum nächsten Rand) und wird bei
  // einer Größenänderung des Fensters nie überschrieben — geklemmt wird nur
  // die Anzeige. Sonst wanderte der Knopf nach einem kurzen Verkleinern
  // (Edge-Seitenleiste, Zoom, Andocken) dauerhaft in die Fenstermitte (T-273).
  const [vp, setVp] = React.useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const [anker, setAnker] = React.useState<KnopfAnker | null>(() => ladePos(window.innerWidth, window.innerHeight));
  const [ziehPos, setZiehPos] = React.useState<KnopfPos | null>(null);
  const pos = ziehPos ?? (anker ? posAus(anker, vp.w, vp.h) : null);
  const zug = React.useRef<{ id: number; sx: number; sy: number; x: number; y: number; zieht: boolean } | null>(null);
  const gezogen = React.useRef(false);
  React.useEffect(() => {
    const neu = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    alleFenster.addEventListener("resize", neu);
    return () => alleFenster.removeEventListener("resize", neu);
  }, []);
  const ziehStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const r = e.currentTarget.getBoundingClientRect();
    zug.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, x: r.left, y: r.top, zieht: false };
  };
  const ziehen = (e: React.PointerEvent<HTMLButtonElement>) => {
    const z = zug.current;
    if (!z || z.id !== e.pointerId) return;
    const dx = e.clientX - z.sx;
    const dy = e.clientY - z.sy;
    if (!z.zieht && Math.hypot(dx, dy) < ZIEH_SCHWELLE) return;
    if (!z.zieht) { z.zieht = true; e.currentTarget.setPointerCapture?.(e.pointerId); }
    setZiehPos(begrenze({ x: z.x + dx, y: z.y + dy }, window.innerWidth, window.innerHeight));
  };
  const ziehEnde = (e: React.PointerEvent<HTMLButtonElement>) => {
    const z = zug.current;
    zug.current = null;
    if (!z || !z.zieht) return;
    gezogen.current = true; // den folgenden Klick schlucken
    const w = window.innerWidth;
    const h = window.innerHeight;
    const ende = begrenze({ x: z.x + (e.clientX - z.sx), y: z.y + (e.clientY - z.sy) }, w, h);
    const a = ankerAus(ende, w, h);
    setVp({ w, h });
    setAnker(a);
    setZiehPos(null);
    speicherePos(a);
  };

  // Seitenverlauf: jede Route und (nach Klicks) jedes Fenster/jeder Tab — nur
  // im Speicher dieses Tabs, verschickt wird er erst beim Melden.
  React.useEffect(() => beobachteKlicks(), []);
  React.useEffect(() => {
    const t = window.setTimeout(erfasseStation, 400);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.search]);

  return (
    <>
      {!kontext && (
        <button
          type="button"
          data-ticket-ignore
          className={`kvp-rd tk-melden${anrufOffen && !pos ? " tk-melden--ueber-anruf" : ""}${isPhone && fensterOffen && !pos ? " tk-melden--fenster-phone" : ""}${pos ? " tk-melden--verschoben" : ""}`}
          style={pos ? { left: pos.x, top: pos.y } : undefined}
          title="Problem oder Idee melden (zum Verschieben ziehen)"
          aria-label="Problem oder Idee melden"
          onPointerDown={ziehStart}
          onPointerMove={ziehen}
          onPointerUp={ziehEnde}
          onPointerCancel={() => { zug.current = null; setZiehPos(null); }}
          onClick={() => {
            if (gezogen.current) { gezogen.current = false; return; }
            setKontext(kontextJetzt(location.pathname + location.search));
          }}
        >
          {I.warnung}
        </button>
      )}
      {kontext && (
        <MeldeDialog
          kontext={kontext}
          onSchliessen={() => setKontext(null)}
          onGesendet={(id) => { setKontext(null); setGemeldet(id); }}
        />
      )}
      <Toast
        open={gemeldet != null}
        tone="success"
        message={gemeldet != null ? `T-${gemeldet} gemeldet, danke!` : ""}
        onClose={() => setGemeldet(null)}
      />
      {gemeldet != null && (
        <button type="button" className="kvp-rd tk-toast-link" data-ticket-ignore
          onClick={() => { navigate(`/einstellungen/tickets/${gemeldet}`); setGemeldet(null); }}>
          Ticket ansehen
        </button>
      )}
    </>
  );
}
